import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, Download, Share2, Tag, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { Infographie } from '@/hooks/useInfographies';
import { NewsletterSection } from '@/components/home/NewsletterSection';
import { useTranslation } from 'react-i18next';

export function InfographieDetailPage() {
  const { slug, locale } = useParams<{ slug: string; locale: string }>();
  const { t, i18n } = useTranslation('infographieDetail');
  const currentLocale = locale || i18n.language || 'fr';
  const [infographie, setInfographie] = useState<Infographie | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchInfographie() {
      if (!slug) return;

      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .from('infographies')
          .select('*')
          .eq('slug', slug)
          .eq('status', 'published')
          .single();

        if (fetchError) throw fetchError;
        setInfographie(data);

        // Incrémenter le compteur de vues
        if (data) {
          await supabase
            .from('infographies')
            .update({ view_count: (data.view_count || 0) + 1 })
            .eq('id', data.id);
        }
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    }

    fetchInfographie();
  }, [slug]);

  const handleDownload = async () => {
    if (!infographie) return;

    // Incrémenter le compteur de téléchargements
    await supabase
      .from('infographies')
      .update({ download_count: (infographie.download_count || 0) + 1 })
      .eq('id', infographie.id);

    // Télécharger l'image avec fetch + blob
    try {
      const response = await fetch(infographie.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${infographie.slug}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur lors du téléchargement:', err);
      // Fallback: ouvrir dans un nouvel onglet
      window.open(infographie.image_url, '_blank');
    }
  };

  const handleShare = async () => {
    if (!infographie) return;

    const shareUrl = window.location.href;
    const shareText = t('shareText', { title: infographie.title });

    if (navigator.share) {
      try {
        await navigator.share({
          title: infographie.title,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        // Fallback: copier dans le presse-papier
        navigator.clipboard.writeText(shareUrl);
        alert(t('actions.linkCopied'));
      }
    } else {
      // Fallback: copier dans le presse-papier
      navigator.clipboard.writeText(shareUrl);
      alert(t('actions.linkCopied'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
        <span className="ml-3 text-aspexit-gray">{t('loading')}</span>
      </div>
    );
  }

  if (error || !infographie) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-red-500 mb-4">{t('error')}</p>
        <Link
          to={`/${currentLocale}/prestations/infographies`}
          className="text-[#FF6B6B] hover:underline flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('back')}
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#FF6B6B] to-[#e05555] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Breadcrumb & Back */}
            <Link
              to={`/${currentLocale}/prestations/infographies`}
              className="inline-flex items-center gap-2 text-sm mb-6 opacity-90 hover:opacity-100 transition-opacity"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('back')}
            </Link>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-heading font-extrabold mb-4">
              {infographie.title}
            </h1>

            {/* Description */}
            <p className="text-xl opacity-95 mb-4 max-w-3xl">
              {infographie.description}
            </p>

            {/* Category */}
            {infographie.category && (
              <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-semibold mb-6">
                {infographie.category}
              </span>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleDownload}
                className="px-6 py-3 bg-white text-[#FF6B6B] rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                {t('actions.download')}
              </button>
              <button
                onClick={handleShare}
                className="px-6 py-3 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2"
              >
                <Share2 className="w-5 h-5" />
                {t('actions.share')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Image */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gray-100 rounded-xl overflow-hidden shadow-2xl">
              <img
                src={infographie.image_url}
                alt={infographie.title}
                className="w-full h-auto"
              />
            </div>

            {/* Tags */}
            {infographie.tags && infographie.tags.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2 items-center">
                <Tag className="w-5 h-5 text-aspexit-gray" />
                {infographie.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-aspexit-gray text-sm rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#FF6B6B] to-[#e05555] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-lg mb-8 opacity-90">
              {t('cta.description')}
            </p>
            <Link
              to={`/${currentLocale}/contact`}
              className="inline-block px-8 py-4 bg-white text-[#FF6B6B] rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              {t('cta.button')}
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />
    </div>
  );
}
